/**
 * useConfirmDialog — remplace `window.confirm()` et `window.prompt()` (R13).
 *
 * Permet une migration ultra-rapide :
 *
 *   const { confirm, prompt, dialogElement } = useConfirmDialog();
 *
 *   // À la place de `if (!confirm("Supprimer ?")) return;`
 *   const ok = await confirm({ title: 'Supprimer ?', description: '...', danger: true });
 *   if (!ok) return;
 *
 *   // À la place de `const c = prompt("Commentaire ?");`
 *   const c = await prompt({ title: 'Commentaire', multiline: true });
 *   if (c === null) return; // user a cliqué Annuler
 *
 *   // Dans le JSX, placer dialogElement au niveau du composant :
 *   return <>{...vos enfants}{dialogElement}</>;
 *
 * Le hook utilise un Promise + state interne pour gérer le résultat de
 * façon impérative comme les modals natives, sans React Context.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { ConfirmDialog, PromptDialog } from '@/components/ui/Dialog';

type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type PromptOptions = {
  title: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  maxLength?: number;
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
};

type State =
  | { kind: 'closed' }
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (ok: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (value: string | null) => void };

export function useConfirmDialog(): {
  /** Affiche une confirmation. Retourne true si OK, false si annulé. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Affiche une saisie de texte. Retourne la valeur ou null si annulé. */
  prompt: (opts: PromptOptions) => Promise<string | null>;
  /** Élément React à placer dans le JSX (juste avant le return final). */
  dialogElement: ReactNode;
} {
  const [state, setState] = useState<State>({ kind: 'closed' });

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ kind: 'confirm', opts, resolve });
    });
  }, []);

  const prompt = useCallback((opts: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({ kind: 'prompt', opts, resolve });
    });
  }, []);

  let dialogElement: ReactNode = null;

  if (state.kind === 'confirm') {
    dialogElement = (
      <ConfirmDialog
        open
        onConfirm={() => {
          state.resolve(true);
          setState({ kind: 'closed' });
        }}
        onCancel={() => {
          state.resolve(false);
          setState({ kind: 'closed' });
        }}
        title={state.opts.title}
        description={state.opts.description}
        confirmLabel={state.opts.confirmLabel}
        cancelLabel={state.opts.cancelLabel}
        danger={state.opts.danger}
      />
    );
  } else if (state.kind === 'prompt') {
    dialogElement = (
      <PromptDialog
        open
        onSubmit={(value) => {
          state.resolve(value);
          setState({ kind: 'closed' });
        }}
        onCancel={() => {
          state.resolve(null);
          setState({ kind: 'closed' });
        }}
        title={state.opts.title}
        description={state.opts.description}
        placeholder={state.opts.placeholder}
        defaultValue={state.opts.defaultValue}
        multiline={state.opts.multiline}
        maxLength={state.opts.maxLength}
        required={state.opts.required}
        confirmLabel={state.opts.confirmLabel}
        cancelLabel={state.opts.cancelLabel}
      />
    );
  }

  return { confirm, prompt, dialogElement };
}
